import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType, FormlyFieldProps } from '@ngx-formly/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BaseDataService } from '@core/services/base-data.service';
import { Observable, Subject } from 'rxjs';
import { BaseDataKey } from '@features/client/order-workflow/enums/base-data-key.enum';
import { BaseDataValueMapItem } from '@core/models/base-data-value-map-item.model';
import { delay, distinctUntilChanged, filter, first, map, mergeMap, shareReplay, startWith } from 'rxjs/operators';
import { get, isEmpty } from 'lodash';
import { InputType } from '@features/client/form-builder/enums/input-type.enum';
import { ProductConfigurationFacade } from '@features/client/order-workflow/facades/product-configuration.facade';
import { OrderProduct } from '@features/client/order-workflow/models/order-product.model';
import { ColorAttributes } from '@features/client/order-workflow/enums/color-attributes.enum';
import { ColorSystemPickerComponent } from '@features/client/order-workflow/components/color-system-input/color-system-picker/color-system-picker.component';
import { AttributeBaseDataService } from '@core/services/attribute-base-data.service';
import { CapturumDropdownComponent } from '@capturum/ui/dropdown';
import { AsyncPipe } from '@angular/common';
import { FormTooltipComponent } from '@shared/components/form-tooltip/form-tooltip.component';

@Component({
  selector: 'app-color-system-input',
  templateUrl: './color-system-input.component.html',
  styleUrls: ['./color-system-input.component.scss'],
  imports: [
    CapturumDropdownComponent,
    ReactiveFormsModule,
    AsyncPipe,
    FormTooltipComponent,
    ColorSystemPickerComponent,
  ],
})
export class ColorSystemInputComponent extends FieldType implements OnInit {
  @ViewChild(ColorSystemPickerComponent) public colorSystemPicker: ColorSystemPickerComponent;

  public readonly colorCategoryAttributeName: string = 'color_system_base_data_value_id';

  public colorSystemCategories$: Observable<BaseDataValueMapItem[]>;
  public selectedCategory$: Subject<BaseDataValueMapItem & { type: InputType; defaultValue: any }> = new Subject<
    BaseDataValueMapItem & { type: InputType; defaultValue: any }
  >();

  public formGroup: FormGroup;
  public subType: InputType;
  public InputType: typeof InputType = InputType;
  public orderProduct: OrderProduct;
  public orderProductId: string;
  public selectedColorSystem: BaseDataValueMapItem;

  private selectedInputType: string;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly baseDataService: BaseDataService,
    private readonly attributeBaseDataService: AttributeBaseDataService,
    private readonly productConfigurationFacade: ProductConfigurationFacade,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.colorSystemCategories$ = this.attributeBaseDataService
      .listBaseData(BaseDataKey.baseColorCategory)
      .pipe(shareReplay(1), delay(0));

    this.orderProduct = this.productConfigurationFacade.getOrderProductSnapshot();
    this.orderProductId = this.orderProduct?.id;

    this.ensureFieldControl();
    this.initializeForm();
    this.changeFormValidation();

    this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter((value) => {
          return !value;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.formControl.setValue(this.formGroup?.value);
      });
  }

  public handleColorSystemChanges(): void {
    this.formGroup
      .get(this.colorCategoryAttributeName)
      .valueChanges.pipe(
        startWith(this.formGroup.get(this.colorCategoryAttributeName)?.value),
        mergeMap((colorSystemValue) => {
          return this.colorSystemCategories$.pipe(
            map((colorCategories) => {
              return { colorSystemValue, colorCategories };
            }),
            shareReplay(1),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ colorSystemValue, colorCategories }) => {
        this.selectedColorSystem = colorCategories.find((colorSystem: BaseDataValueMapItem) => {
          return colorSystem?.value === colorSystemValue;
        });

        const selectedInputType = this.getInputType(this.selectedColorSystem?.value_key);
        const defaultValues = this.getDefaultValuesPerInputType(this.field?.defaultValue, selectedInputType);

        this.selectedInputType = selectedInputType;

        this.updateColorForm(defaultValues);
        this.resetColors(this.selectedColorSystem?.value_key);

        this.selectedCategory$.next({
          ...this.selectedColorSystem,
          type: selectedInputType,
          defaultValue: defaultValues,
        });
      });
  }

  public changeColorSystemType(): void {
    if (this.colorSystemPicker) {
      this.colorSystemPicker.resetPositions();
    }
  }

  public updateColorForm(childFormValues: any): void {
    this.resetColorAttributes(childFormValues);

    this.formGroup.get('colorAttributes').reset();
    this.formGroup.get('colorAttributes').setValue(childFormValues);

    const colorAttributes = {
      [this.colorCategoryAttributeName]: this.formGroup.get(this.colorCategoryAttributeName)?.value,
      ...this.formGroup.get('colorAttributes')?.value,
    };

    this.form?.get(`${this.field.key}`)?.setValue(colorAttributes);
  }

  // TODO: Move to BE side by implementing has_color_system property.
  protected hasColorSystem(selectedColorSystem: string): boolean {
    switch (selectedColorSystem) {
      case 'color_system_sr_vivodent':
      case 'color_system_vita_classic':
      case 'color_system_vita_3d':
        return true;
      default:
        return false;
    }
  }

  protected getCommonValidations(field: FormlyFieldProps): ValidatorFn[] | null {
    return field?.required ? [Validators.required] : null;
  }

  // ngx-formly v6 leaves this custom field's control detached from the parent form once the field is revealed
  // via dependencies, so form.get(field.key) is null and validity never reflects the colour choice.
  // Attach the existing control and gate it on an actually-selected category (a bare object passes Validators.required).
  private ensureFieldControl(): void {
    const form = this.form as FormGroup;
    const key = this.field?.key as string;

    if (!key) {
      return;
    }

    if (!form.get(key) && this.formControl) {
      form.addControl(key, this.formControl);
    }

    const validator = this.colorSystemRequiredValidator();

    if (validator) {
      form.get(key)?.addValidators(validator);
      form.get(key)?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private colorSystemRequiredValidator(): ValidatorFn | null {
    if (!this.field?.props?.required) {
      return null;
    }

    return (control: AbstractControl): ValidationErrors | null => {
      return control.value?.[this.colorCategoryAttributeName] ? null : { required: true };
    };
  }

  private initializeForm(): void {
    this.colorSystemCategories$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
      const firstValue = { color_system_base_data_value_id: values[0]?.value };
      let defaultValue = !isEmpty(this.field?.defaultValue) ? this.field?.defaultValue : null;

      if (!defaultValue && this.hasColorSystem(this.selectedColorSystem?.value_key)) {
        defaultValue = firstValue;
      }

      this.formGroup = new FormGroup({
        [this.colorCategoryAttributeName]: new FormControl(
          get(defaultValue || null, this.colorCategoryAttributeName),
          this.getCommonValidations(this.field?.props),
        ),
        colorAttributes: new FormControl(null, this.getCommonValidations(this.field?.props)),
      });

      this.formControl.setValue(this.formGroup?.value);
      this.formControl.updateValueAndValidity();
      this.formGroup.updateValueAndValidity();
      this.handleColorSystemChanges();
    });
  }

  private getInputType(selectedColorSystem: string): InputType {
    return this.hasColorSystem(selectedColorSystem) ? InputType.colorSystem : null;
  }

  private getDefaultValuesPerInputType(defaultValues: any, selectedType: InputType): any {
    if (!defaultValues) {
      return;
    }

    switch (selectedType) {
      case InputType.colorSystem:
        return Object.entries(defaultValues).reduce((acc, entry) => {
          if (Object.values(ColorAttributes).includes(entry[0] as any)) {
            return { ...acc, [entry[0]]: entry[1] };
          }
        }, {});
      default:
        return null;
    }
  }

  private resetColorAttributes(childFormValues: any): void {
    const formGroup = this.form as FormGroup;

    Object.values(ColorAttributes).forEach((colorControl) => {
      return formGroup.removeControl(colorControl);
    });

    if (!childFormValues) {
      formGroup.addControl(ColorAttributes.base, new FormControl(null, this.getCommonValidations(this.field?.props)));
    } else {
      Object.keys(childFormValues).forEach((colorControl) => {
        formGroup.addControl(colorControl, new FormControl(null, this.getCommonValidations(this.field?.props)));

        formGroup.get(colorControl).setValue(childFormValues[colorControl]);
      });
    }
  }

  private resetColors(selectedColorSystem: string): void {
    if (!this.hasColorSystem(selectedColorSystem)) {
      this.formControl.setValue({
        [this.colorCategoryAttributeName]: this.formGroup.get(this.colorCategoryAttributeName)?.value,
      });
    }
  }

  private changeFormValidation(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((form) => {
      const colorSystemShade = Object.keys(form).find((shade) => {
        return shade.includes('_colorsystem_shade');
      });

      this.checkInputType(form[colorSystemShade]);
    });
  }

  private checkInputType(lvlColors: Record<string, string> | boolean): void {
    this.insertValidation(lvlColors);

    this.selectedCategory$.pipe(first()).subscribe((category) => {
      this.selectedInputType = category.type;

      this.insertValidation(lvlColors);
    });
  }

  private insertValidation(lvlColors: Record<string, string> | boolean): void {
    this.productConfigurationFacade.colorSystemHiddenFields[`${this.field.key}`] = this.field.hide;
    const isAllColorSystemFieldsHidden = Object.values(this.productConfigurationFacade.colorSystemHiddenFields).every(
      Boolean,
    );
    const isColorSystemTypeSelected = !!this.selectedInputType;
    const isColorSelected = lvlColors
      ? lvlColors[ColorAttributes.base] ||
        lvlColors[ColorAttributes.top] ||
        lvlColors[ColorAttributes.middle] ||
        lvlColors[ColorAttributes.bottom]
      : false;
    const clearValidation =
      isAllColorSystemFieldsHidden || !isColorSystemTypeSelected || (isColorSystemTypeSelected && isColorSelected);

    if (this.form?.value && ColorAttributes.base in this.form.value) {
      this.form.controls[ColorAttributes.base].setValidators(
        clearValidation ? [] : this.getCommonValidations(this.field.props),
      );
      this.form.controls[ColorAttributes.base].updateValueAndValidity({ emitEvent: false });
      this.form.updateValueAndValidity({ emitEvent: false });
    }
  }
}
